/**
 * Positionen aus einem ANDEREN gespeicherten Angebot übernehmen
 * ("Rosinenpicken", Wunsch Malermeister): reine Abbildungsfunktion
 * Quell-QuoteItem → (Position, QuoteItem) für appendPosition.
 *
 * Kein DOM, kein fetch — vollständig unter Node testbar
 * (tests/quote-position-copy.test.ts). Der Dialog
 * client/src/components/quote-position-import-dialog.tsx ist nur
 * Darstellung über diesen Funktionen (Muster ifc-import-mapping.ts).
 *
 * WAS WANDERT MIT (der Wert des alten Angebots):
 * - Texte: name (Kurztext), beschreibung, longText
 * - einheit, menge, preis, preisFehlt (fehlender Preis bleibt "–", wird NIE 0,00)
 * - umsatzsteuer
 * - kalkulation als TIEFE Kopie — inklusive mengenBezug! Ohne mengenBezug
 *   würde eine 'einheit'-Kalkulation im Ziel als 'gesamt' (Bestands-Default)
 *   gelesen und der EP still durch die Menge geteilt (der 522-€→52,20-€-Bug).
 *   WICHTIG: ein ABWESENDER mengenBezug ist selbst Bedeutungsträger
 *   ('gesamt'-Default für Bestandsdaten) und wird verbatim abwesend gelassen,
 *   nicht zu 'einheit' erfunden.
 * - Positionsart (isBedarfsposition / isAlternativposition)
 * - Gliederung: gewerk (Titel) + untergewerkName (Untertitel) — appendPosition
 *   hängt ein im Ziel unbekanntes Gewerk ans ENDE der gewerkOrder an; der
 *   Nutzer sortiert danach um, statt dass Positionen still verschwinden.
 *
 * WAS NICHT MITWANDERT (im Ziel wäre es gelogen oder fremd):
 * - positionNumber / gewerkNr / untergewerkNr / parentPositionNumber /
 *   hierarchyLevel: die OZ des Quell-LVs; das Ziel nummeriert selbst
 *   (renumberPositions / applyQuoteNumbering).
 * - id / originalId: das Ziel vergibt eine freie ID.
 * - budgetTaskId: Verweis auf das FREMDE Softec-Projekt — würde
 *   Rechnungs-/Leistungsstand-Sync auf das falsche Projekt lenken.
 * - aufmass: Aufmaßzeilen der alten Baustelle sind im neuen Angebot falsch.
 * - mengeHerkunft: "Menge aus IFC-Modell X" bezog sich auf das ALTE Bauvorhaben
 *   — im neuen Angebot wäre der Vermerk gelogen. Ersetzt durch
 *   positionsHerkunft ("aus Angebot AN-… übernommen").
 * - rabattTyp / rabattWert: Nachlässe sind eine Vereinbarung des alten
 *   Angebots, keine Eigenschaft der Leistung.
 *
 * Stattdessen bekommt die Kopie einen positionsHerkunft-Vermerk (Preisstand!),
 * den updatePosition bei manueller Preisänderung löscht (Muster mengeHerkunft).
 */

import type {
  Position,
  PositionsHerkunft,
  PositionsKalkulation,
  QuoteItem,
} from '@shared/schema';

export interface QuellAngebotInfo {
  /** Angebotsnummer des Quell-Angebots (header.angebot_nr) */
  angebotNr: string;
  /** Angebotsdatum des Quell-Angebots (ISO) — der "Preisstand" */
  datum?: string;
  kunde?: string;
  slot?: number;
}

export interface KopierOptionen {
  /** freie Positions-ID im Ziel-Angebot */
  id: number;
  quelle: QuellAngebotInfo;
}

export interface PositionsPaar {
  position: Position;
  quoteItem: QuoteItem;
}

/** Titel-/Gliederungszeilen (isHeader) sind keine Leistungen — nicht kopierbar. */
export function istKopierbar(item: QuoteItem): boolean {
  return item.isHeader !== true;
}

/**
 * Tiefe Kopie der Kalkulation (plain JSON) — Quelle und Ziel dürfen sich
 * danach nicht mehr teilen (sonst editiert die Ziel-Kalkulation still das
 * Quell-Angebot mit). mengenBezug wandert dabei verbatim mit (siehe Kopf).
 */
function kopiereKalkulation(
  kalkulation: PositionsKalkulation | undefined,
): PositionsKalkulation | undefined {
  if (!kalkulation) return undefined;
  return JSON.parse(JSON.stringify(kalkulation)) as PositionsKalkulation;
}

function normalisierterText(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Erzeugt aus einem Quell-QuoteItem das Paar (Position, QuoteItem) für
 * appendPosition im Ziel-Angebot. Wirft bei Gliederungszeilen (isHeader) —
 * die UI muss vorher mit istKopierbar filtern.
 */
export function kopiereQuoteItemAusAngebot(
  quelle: QuoteItem,
  optionen: KopierOptionen,
): PositionsPaar {
  if (!istKopierbar(quelle)) {
    throw new Error(
      `Gliederungszeile "${quelle.name || quelle.beschreibung || quelle.positionNumber || '?'}" ist keine Position und kann nicht übernommen werden.`,
    );
  }

  const herkunft: PositionsHerkunft = {
    quelle: 'angebot',
    angebotNr: optionen.quelle.angebotNr,
    ...(optionen.quelle.datum ? { datum: optionen.quelle.datum } : {}),
    ...(optionen.quelle.kunde ? { kunde: optionen.quelle.kunde } : {}),
    ...(optionen.quelle.slot !== undefined ? { slot: optionen.quelle.slot } : {}),
  };

  const name = normalisierterText(quelle.name);
  const beschreibung = normalisierterText(quelle.beschreibung);
  const longText = normalisierterText(quelle.longText);
  // Titel: item.gewerk ist die Editor-Wahrheit; gewerkName ist das
  // GAEB-Hierarchie-Metadatum — nur als Fallback, wenn gewerk fehlt.
  const gewerk = (quelle.gewerk || quelle.gewerkName || '').trim();
  const untergewerkName = normalisierterText(quelle.untergewerkName).trim();
  const einheit = normalisierterText(quelle.einheit);
  const preisRoh = Number(quelle.preis);
  const preis = Number.isFinite(preisRoh) ? preisRoh : 0;
  const preisFehlt = quelle.preisFehlt === true ? true : undefined;
  const mengeRoh = Number(quelle.menge);
  const menge = Number.isFinite(mengeRoh) && mengeRoh > 0 ? mengeRoh : 0;
  const umsatzsteuerRoh = Number(quelle.umsatzsteuer);
  const umsatzsteuer = Number.isFinite(umsatzsteuerRoh) ? umsatzsteuerRoh : 19;
  const isBedarfsposition = quelle.isBedarfsposition === true ? true : undefined;
  const isAlternativposition = quelle.isAlternativposition === true ? true : undefined;

  const position: Position = {
    id: optionen.id,
    originalId: String(optionen.id),
    gewerk: gewerk || null,
    untergewerkName: untergewerkName || undefined,
    name: name || null,
    beschreibung,
    longText: longText || undefined,
    einheit,
    preis,
    ...(preisFehlt ? { preisFehlt } : {}),
    umsatzsteuer,
    kalkulation: kopiereKalkulation(quelle.kalkulation) ?? null,
    ...(isBedarfsposition ? { isBedarfsposition } : {}),
    ...(isAlternativposition ? { isAlternativposition } : {}),
    positionsHerkunft: herkunft,
  };

  const quoteItem: QuoteItem = {
    id: optionen.id,
    originalId: String(optionen.id),
    name: name || undefined,
    beschreibung,
    ...(longText ? { longText } : {}),
    gewerk: gewerk || undefined,
    ...(untergewerkName ? { untergewerkName } : {}),
    einheit,
    preis,
    ...(preisFehlt ? { preisFehlt } : {}),
    umsatzsteuer,
    menge,
    ...(position.kalkulation ? { kalkulation: kopiereKalkulation(quelle.kalkulation) } : {}),
    ...(isBedarfsposition ? { isBedarfsposition } : {}),
    ...(isAlternativposition ? { isAlternativposition } : {}),
    positionsHerkunft: { ...herkunft },
  };

  return { position, quoteItem };
}

/** "aus Angebot AN-2026-014 vom 12.05.2026" — Badge-Tooltip + Ziel-Vermerk. */
export function herkunftsLabel(herkunft: PositionsHerkunft): string {
  const datum = formatiereDatum(herkunft.datum);
  return `aus Angebot ${herkunft.angebotNr}${datum ? ` vom ${datum}` : ''}`;
}

/** ISO-Datum → deutsche Anzeige; ungültige/fehlende Eingaben ehrlich leer. */
export function formatiereDatum(iso: string | undefined): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
